import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import FAQSchema from '../../FAQSchema';
// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const CarLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(5);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let currentYear = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is a car loan EMI?',
        answer: 'It’s the fixed amount you pay monthly to the bank or NBFC, which includes both principal and interest, until the loan is repaid in full.',
      },
      {
        question: 'How is car loan EMI calculated?',
        answer: 'It is calculated using a formula based on the principal, interest rate, and loan tenure.',
      },
      {
        question: 'Is the EMI amount fixed throughout the tenure?',
        answer: 'Yes, the EMI remains the same, but the composition of interest and principal changes.',
      },
      {
        question: 'Can I reduce my EMI?',
        answer: 'Yes, by choosing a longer tenure, negotiating a lower interest rate, or making a larger down payment.',
      },
      {
        question: 'Does the calculator consider processing fees?',
        answer: 'No, the EMI calculator doesn’t factor in fees, but lenders may charge one-time processing charges.',
      },
      {
        question: 'Will my EMI change if interest rates fluctuate?',
        answer: 'If you’re on a fixed interest rate loan, it stays the same. In floating-rate loans, EMI or tenure may change.',
      },
      {
        question: 'What is an amortization schedule?',
        answer: 'It’s a table that shows how each EMI is split between principal and interest over the loan tenure.',
      },
      {
        question: 'Can I prepay my car loan?',
        answer: 'Yes, but check with your lender for prepayment penalties or terms.',
      },
      {
        question: 'Is a higher down payment better?',
        answer: 'Yes, it reduces the loan amount, interest burden, and EMI.',
      },
      {
        question: 'How accurate is TheFinMaster’s EMI calculator?',
        answer: 'It uses standard formulas and is highly accurate for estimation purposes.',
      },
      {
        question: 'Do I need to provide personal information to use the calculator?',
        answer: 'No. It’s a free tool that doesn’t require login or personal data.',
      },
      {
        question: 'What’s the best tenure for a car loan?',
        answer: 'It depends on your repayment capacity. Shorter tenures mean higher EMIs but lower total interest.',
      },
      {
        question: 'Can I use this calculator for used car loans?',
        answer: 'Yes. The calculator works for both new and used car loans, as long as you input the correct data.',
      },
      {
        question: 'Is GST included in the EMI?',
        answer: 'GST is typically applied on processing fees, not on the EMI. The calculator doesn’t factor in taxes.',
      },
      {
        question: 'Why should I calculate my EMI before applying for a loan?',
        answer: 'It helps in financial planning, reduces surprises, and ensures the loan fits your budget.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    const calculators = [
      {
        name: 'SBI Car Loan Calculator',
        path: '/financial-tools-calculators/sbi-car-loan-emi-calculator',
        image: '/assets/sbii.png',
      },
      {
        name: 'Axis Car Loan Calculator',
        path: '/financial-tools-calculators/axis-car-loan-emi-calculator',
        image: '/assets/ax.png',
      },
      {
        name: 'ICICI Car Loan Calculator',
        path: '/financial-tools-calculators/icici-car-loan-emi-calculator',
        image: '/assets/icc.png',
      },
      {
        name: 'HDFC Car Loan Calculator',
        path: '/financial-tools-calculators/hdfc-car-loan-emi-calculator',
        image: '/assets/hdd.png',
      },
      {
        name: 'Canara Car Loan Calculator',
        path: '/financial-tools-calculators/canara-car-loan-emi-calculator',
        image: '/assets/can.png',
      },
      {
        name: 'PNB Car Loan Calculator',
        path: '/financial-tools-calculators/pnb-car-loan-emi-calculator',
        image: '/assets/pn.png',
      },
      {
        name: 'Union Car Loan Calculator',
        path: '/financial-tools-calculators/Union-car-loan-emi-calculator',
        image: '/assets/un.png',
      },
      {
        name: 'BOB Car Loan Calculator',
        path: '/financial-tools-calculators/bank-of-baroda-car-loan-emi-calculator',
        image: '/assets/bb.png',
      },
      {
        name: 'Dhanlaxmi Car Loan Calculator',
        path: '/financial-tools-calculators/dhanlaxmi-bank-car-loan-emi-calculator',
        image: '/assets/dhan.png',
      },
      {
        name: 'BOM Car Loan Calculator',
        path: '/financial-tools-calculators/bank-of-maharashtra-car-loan-emi-calculator',
        image: '/assets/b.png',
      },
     
    ];
  return (
    <>
     <Helmet>
         <title>Car Loan EMI Calculator – Monthly Installment & Interest</title>
        <meta  name='description' content='Use our car loan EMI calculator to determine your monthly installments and total payment schedule easily.'/>
        <meta name='keywords' content='car loan emi calculator, car emi calculator, auto emi calculator, vehicle emi calculator, car finance emi calculator, automobile loan emi calculator, monthly emi calculator car loan' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/car-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Car Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav>
        <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Car Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>


    <div className="p-4 sm:p-8 bg-white">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
      Car Loan EMI Calculator 
      </h2>

         <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {calculators.map((calc, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.04 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Link
              to={calc.path}
              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
            >
              <img
                src={calc.image}
                alt={calc.name}
                className="w-16 h-16 object-contain mb-2"
              />
              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                {calc.name}
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your Car Loan Smartly with TheFinMaster’s EMI Calculator
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Buying a car is often one of the biggest financial decisions individuals make. Whether it’s your first vehicle or an upgrade to something more premium, knowing how much you’ll pay each month is crucial to maintaining financial stability. That’s where TheFinMaster's Car Loan EMI Calculator comes in. Designed to simplify your loan planning process, this tool empowers you to make data-backed decisions with clarity and ease.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Car Loan EMI?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A car loan EMI (Equated Monthly Installment) is the fixed amount you pay every month to your lender until your car loan is fully repaid. Each EMI consists of two components — the principal amount and the interest on the borrowed amount.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Understanding your EMI is essential before taking a loan because it helps you estimate your monthly outgo, evaluate your repayment capacity, and avoid any surprises down the road. TheFinMaster’s EMI calculator offers a simple and effective way to simulate this repayment pattern in seconds.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Car Loan EMI Calculator?
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Accurate & Instant Calculations:</strong> Manual EMI calculations using formulas can be tedious and error-prone. With our online EMI calculator, you get instant and error-free results, ensuring complete accuracy in your financial planning.</li>
              <li><strong>Plan Your Finances Better:</strong> Knowing your EMI before taking a loan helps you structure your monthly budget effectively. You can make adjustments based on your cash flow and avoid committing to an amount you can’t comfortably afford.</li>
              <li><strong>Compare Loan Scenarios:</strong> By adjusting the loan amount, interest rate, or tenure, you can evaluate multiple scenarios instantly. Whether you want a longer tenure for lower EMIs or a shorter term to save on interest, the calculator lets you weigh all options.</li>
              <li><strong>Get a Transparent Cost Breakdown:</strong> The calculator provides a detailed cost breakdown — including total interest payable and total repayment amount — to help you understand the full financial impact of your car loan.</li>
              <li><strong>Easy & Accessible Tool:</strong> TheFinMaster’s calculator is free to use, doesn’t require any personal data, and can be accessed from desktop or mobile, anytime and anywhere.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Car Loan EMI Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator uses a standardized EMI formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>EMI = [P x R x (1+R)^N] / [(1+R)^N – 1]</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P is the principal loan amount</li>
              <li>R is the monthly interest rate (annual rate divided by 12 and converted to a decimal)</li>
              <li>N is the number of monthly installments (loan tenure in months)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s say you borrow ₹10,00,000 for 7 years at an annual interest rate of 10%.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Loan Tenure = 84 months</li>
              <li>Monthly Interest Rate = 10% ÷ 12 = 0.833%</li>
              <li>EMI = ₹16,602</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You end up repaying a total of ₹13,94,568 — which includes ₹3,94,568 in interest over the 7 years. The EMI amount remains constant each month, but the interest and principal components vary — with interest forming a larger part of the EMI during the initial months.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An amortization schedule is the complete table of periodic loan payments including the principal and interest amount in each payment until the loan is fully repaid. It provides a complete guide to breaking down a credit option such as a car loan into a series of fixed payments. The monthly payment remains the same, but the payment is made up of parts that differ over time. The best thing about an amortization schedule is it is very helpful for banks and financial institutes to keep a complete track of the loans.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Provided below is the amortization schedule for a car loan worth Rs. 8 Lakh for a tenure of 5 years at an interest rate of 9.5%.
            </p>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Month</th>
                    <th className="px-4 py-2 text-left">Principal (Rs.)</th>
                    <th className="px-4 py-2 text-left">Interest (Rs.)</th>
                    <th className="px-4 py-2 text-left">Principal + Interest (Rs.)</th>
                    <th className="px-4 py-2 text-left">Balance to be Paid (Rs.)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">1</td>
                    <td className="px-4 py-2">10468</td>
                    <td className="px-4 py-2">6333</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">789532</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">2</td>
                    <td className="px-4 py-2">10551</td>
                    <td className="px-4 py-2">6250</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">778981</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">3</td>
                    <td className="px-4 py-2">10635</td>
                    <td className="px-4 py-2">6167</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">768346</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">4</td>
                    <td className="px-4 py-2">10719</td>
                    <td className="px-4 py-2">6083</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">757628</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">5</td>
                    <td className="px-4 py-2">10804</td>
                    <td className="px-4 py-2">5998</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">746824</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">6</td>
                    <td className="px-4 py-2">10889</td>
                    <td className="px-4 py-2">5912</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">735935</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">7</td>
                    <td className="px-4 py-2">10975</td>
                    <td className="px-4 py-2">5826</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">724959</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">8</td>
                    <td className="px-4 py-2">11062</td>
                    <td className="px-4 py-2">5739</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">713897</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">9</td>
                    <td className="px-4 py-2">11150</td>
                    <td className="px-4 py-2">5652</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">702747</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">10</td>
                    <td className="px-4 py-2">11238</td>
                    <td className="px-4 py-2">5563</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">691509</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">11</td>
                    <td className="px-4 py-2">11327</td>
                    <td className="px-4 py-2">5474</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">680182</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">12</td>
                    <td className="px-4 py-2">11417</td>
                    <td className="px-4 py-2">5385</td>
                    <td className="px-4 py-2">16801</td>
                    <td className="px-4 py-2">668766</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
             
          <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow mt-6">
    {/* Header */}
    <div className="hidden md:grid grid-cols-5 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Month</div>
      <div className="px-3 py-2 border-r">Principal (Rs.)</div>
      <div className="px-3 py-2 border-r">	Interest (Rs.)	</div>
      <div className="px-3 py-2 border-r">	Principal + Interest (Rs.)</div>

      <div className="px-3 py-2">	Balance to be Paid (Rs.)</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          month: "1",
          prin: "		10468",
          int: "	6333",
          prit: "		16801",
          bal: "	789532",
        },
        {
          month: "2",
          prin: "	10551",
          int: "		6250",
          prit: "	16801",
          bal: "		778981",
        },
        {
          month: "3",
          prin: "	10635",
          int: "		6167",
          prit: "		16801",
          bal: "	768346",
        },
        {
          month: "4",
          prin: "	10719",
          int: "	6083",
          prit: "		16801",
          bal: "		757628",
        },
        {
          month: "5",
          prin: "	10804",
          int: "		5998",
          prit: "		16801",
          bal: "	746824",
        },
        {
          month: "6",
          prin: "	10889",
          int: "		5912",
          prit: "	16801",
          bal: "		735935",
        },
        {
          month: "7",
          prin: "	10975",
          int: "5826",
          prit: "		16801",
          bal: "	724959",
        },
        {
          month: "8",
          prin: "	11062",
          int: "	5739",
          prit: "		16801",
          bal: "	713897",
        },
        {
          month: "9",
          prin: "11150",
          int: "	5652",
          prit: "		16801",
          bal: "	702747",
        },
        {
          month: "10",
          prin: "		11238",
          int: "	5563",
          prit: "	16801",
          bal: "	691509",
        },
        {
          month: "11",
          prin: "	11327",
          int: "	5474",
          prit: "	16801",
          bal: "	680182",
        },
        {
          month: "12",
          prin: "	11417",
          int: "	5385",
          prit: "	16801",
          bal: "	668766",
        },
       
      
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-5 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Month <span className="font-normal">{row.month}</span></p>
            <p className="font-semibold">Principal (Rs.) <span className="font-normal">{row.prin}</span></p>
            <p className="font-semibold">Interest (Rs.) <span className="font-normal">{row.int}</span></p>
            <p className="font-semibold">Principal + Interest (Rs.)<span className="font-normal">{row.prit}</span></p>
            <p className="font-semibold">Balance to be Paid (Rs.)<span className="font-normal">{row.bal}</span></p>
          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.month}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.prin}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.int}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.prit}</div>
          <div className="hidden md:block px-4 py-3">{row.bal}</div>

        </div>
      ))}
    </div>
  </div>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The principal and interest components will respectively increase and decrease proportionately for the entire loan tenure.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinMaster’s Car Loan EMI Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>No Guesswork Involved:</strong> Exact figures based on your inputs.</li>
              <li><strong>User-Friendly Interface:</strong> Just enter a few details to see results.</li>
              <li><strong>Scenario Testing:</strong> Try different interest rates or tenures to see what works best.</li>
              <li><strong>Cost Analysis:</strong> Total repayment and interest payable are shown clearly.</li>
              <li><strong>Enhanced Control:</strong> Adjust your loan based on realistic affordability.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Understanding the EMI Breakdown
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Every EMI consists of two components:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Principal:</strong> The amount that goes towards repaying your loan.</li>
              <li><strong>Interest:</strong> The cost charged by the lender for providing the loan.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              In the early months, a higher portion of your EMI goes toward interest. Gradually, the principal component increases while interest reduces. This is known as the reducing balance method, which is typically how car loan EMIs are structured.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule: See the Bigger Picture
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An amortization schedule is a detailed table showing the split of principal and interest in each EMI across the loan tenure. This helps you understand:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>How much principal is outstanding at any point.</li>
              <li>How interest payments decline over time.</li>
              <li>When your loan will be fully paid off.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinMaster provides amortization insights for users who want to go deeper into their repayment timeline.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors That Affect Your Car Loan EMI
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> Higher loan amounts lead to larger EMIs.</li>
              <li><strong>Interest Rate:</strong> A higher interest rate increases the total cost of borrowing.</li>
              <li><strong>Loan Tenure:</strong> Longer tenure means smaller EMIs but higher total interest.</li>
              <li><strong>Down Payment:</strong> A bigger down payment reduces your loan requirement and EMI.</li>
              <li><strong>Credit Score:</strong> A good credit score can help you get better interest rates.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use a Car Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This calculator is ideal for:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>First-time car buyers planning their first big purchase.</li>
              <li>Budget-conscious families trying to balance monthly finances.</li>
              <li>Auto enthusiasts comparing EMIs for multiple models or variants.</li>
              <li>Loan refinancers exploring better terms or interest rates.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If you are considering taking a car loan, using an EMI calculator is your first step toward informed and responsible borrowing.
            </p>
          </div>

          <h1 className="text-xl font-medium text-gray-700 mt-4">
            “Drive your dream car with confidence! Use TheFinMaster’s Car Loan EMI Calculator to plan your finances smartly!”
          </h1>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default CarLoanEMICalculator;


// import React, { useState } from 'react';
// import {
//   Chart as ChartJS,
//   ArcElement,
//   Tooltip as ChartTooltip,
//   Legend,
// } from 'chart.js';
// import { Pie } from 'react-chartjs-2';

// // Register necessary components
// ChartJS.register(
//   ArcElement,
//   ChartTooltip,
//   Legend
// );

// const CarLoanEMICalculator = () => {
//   const [loanAmount, setLoanAmount] = useState(500000);
//   const [rateOfInterest, setRateOfInterest] = useState(8.5);
//   const [loanTenure, setLoanTenure] = useState(5);
//   const [monthlyEMI, setMonthlyEMI] = useState(0);
//   const [totalInterest, setTotalInterest] = useState(0);
//   const [totalAmount, setTotalAmount] = useState(0);
//   const [activeYear, setActiveYear] = useState(null);

//   const calculateEMI = () => {
//     const monthlyInterestRate = rateOfInterest / 12 / 100;
//     const numberOfPayments = loanTenure * 12;
//     const emi = (loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
//     const totalPayment = emi * numberOfPayments;
//     const totalInterestPaid = totalPayment - loanAmount;

//     setMonthlyEMI(emi.toFixed(2));
//     setTotalInterest(totalInterestPaid.toFixed(2));
//     setTotalAmount(totalPayment.toFixed(2));
//   };

//   const handleLoanAmountChange = (e) => {
//     const value = e.target.value;
//     setLoanAmount(value === '' ? 0 : parseInt(value));
//   };

//   const handleRateOfInterestChange = (e) => {
//     const value = e.target.value;
//     setRateOfInterest(value === '' ? 0 : parseFloat(value));
//   };

//   const handleLoanTenureChange = (e) => {
//     const value = e.target.value;
//     setLoanTenure(value === '' ? 0 : parseInt(value));
//   };

//   const toggleYear = (year) => {
//     setActiveYear(activeYear === year ? null : year);
//   };

//   const data = {
//     labels: ['Principal Amount', 'Total Interest', 'Total Amount'],
//     datasets: [
//       {
//         label: 'Loan Breakdown',
//         data: [loanAmount, totalInterest, totalAmount],
//         backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'],
//       },
//     ],
//   };

//   return (
//     <div className="container mx-auto p-4">
//       <h1 className="text-2xl font-bold mb-4">Car Loan EMI Calculator</h1>
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//         <div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Loan Amount</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="0"
//                 value={loanAmount}
//                 onChange={handleLoanAmountChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
//             </div>
//             <input
//               type="range"
//               min="0"
//               max="10000000"
//               value={loanAmount}
//               onChange={(e) => setLoanAmount(parseInt(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Rate of Interest (p.a.)</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="0"
//                 max="15"
//                 step="0.1"
//                 value={rateOfInterest}
//                 onChange={handleRateOfInterestChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">{rateOfInterest} %</span>
//             </div>
//             <input
//               type="range"
//               min="0"
//               max="15"
//               step="0.1"
//               value={rateOfInterest}
//               onChange={(e) => setRateOfInterest(parseFloat(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 font-semibold">Loan Tenure (years)</label>
//             <div className="flex items-center">
//               <input
//                 type="number"
//                 min="1"
//                 max="30"
//                 value={loanTenure}
//                 onChange={handleLoanTenureChange}
//                 className="w-1/2 p-2 border rounded mr-2"
//               />
//               <span className="text-gray-700">{loanTenure} Yr</span>
//             </div>
//             <input
//               type="range"
//               min="1"
//               max="30"
//               value={loanTenure}
//               onChange={(e) => setLoanTenure(parseInt(e.target.value))}
//               className="w-full mt-2"
//             />
//           </div>
//           <button
//             onClick={calculateEMI}
//             className="bg-blue-500 text-white px-4 py-2 rounded"
//           >
//             Calculate
//           </button>
//         </div>
//         <div>
//           <div className="mb-4">
//             <h2 className="text-xl font-bold">Results</h2>
//             <p>Monthly EMI: ₹ {monthlyEMI}</p>
//             <p>Total Interest: ₹ {totalInterest}</p>
//             <p>Total Amount: ₹ {totalAmount}</p>
//           </div>
//           <div>
//             <h2 className="text-xl font-bold">Breakdown</h2>
//             <div className="flex justify-center">
//               <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="mt-6">
//         <h2 className="text-xl font-bold">Amortization Schedule</h2>
//         <div>
//           {Array.from({ length: loanTenure }, (_, i) => i + 1).map((year) => (
//             <div key={year}>
//               <div
//                 className="cursor-pointer text-gray-700 font-semibold my-2"
//                 onClick={() => toggleYear(year)}
//               >
//                 {activeYear === year ? '-' : '+'} {2023 + year}
//               </div>
//               {activeYear === year && (
//                 <div className="pl-4">
//                   <table className="w-full text-left border-collapse">
//                     <thead>
//                       <tr>
//                         <th className="p-2 border">Month</th>
//                         <th className="p-2 border">Principal Paid</th>
//                         <th className="p-2 border">Interest Charged</th>
//                         <th className="p-2 border">Total Payment</th>
//                         <th className="p-2 border">Balance</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {/* Example amortization data */}
//                       <tr>
//                         <td className="p-2 border">Jan</td>
//                         <td className="p-2 border">₹ 14,149</td>
//                         <td className="p-2 border">₹ 5,416</td>
//                         <td className="p-2 border">₹ 19,566</td>
//                         <td className="p-2 border">₹ 9,85,851</td>
//                       </tr>
//                       {/* Add more rows as needed */}
//                     </tbody>
//                   </table>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CarLoanEMICalculator;

